import { lazy, type ReactElement, Suspense } from 'react';
import { Center, Loader, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import { ErrorBoundary } from './components/ErrorBoundary';
import { env, isProduction } from './utils/env';
import { installChunkPreloadErrorHandler } from './utils/chunkRecovery';

const Unsubscribe = lazy(() =>
  import('./pages/Unsubscribe').then((module) => ({ default: module.Unsubscribe }))
);
const Help = lazy(() => import('./pages/Help').then((module) => ({ default: module.Help })));
const AppRedirect = lazy(() =>
  import('./pages/AppRedirect').then((module) => ({ default: module.AppRedirect }))
);

installChunkPreloadErrorHandler();

Sentry.init({
  dsn: env.SENTRY_DSN,
  environment: env.ENV,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: isProduction() ? 0.1 : 1.0,
  tracePropagationTargets: [
    'localhost',
    /^http:\/\/localhost:\d+/,
    /^https:\/\/[^/]*\.savida\.ai\/api/,
    /^https:\/\/[^/]*\.fly\.dev\/api/,
    env.API_URL,
  ],
  replaysSessionSampleRate: isProduction() ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
});

const LoadingPage = (): ReactElement => (
  <Center h="100vh">
    <Loader size="xl" />
  </Center>
);

const InstallLanding = (): ReactElement => (
  <Suspense fallback={<LoadingPage />}>
    <AppRedirect />
  </Suspense>
);

const App = (): ReactElement => (
  <MantineProvider>
    <ErrorBoundary>
      <Notifications position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route
            path="/unsubscribe"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Unsubscribe />
              </Suspense>
            }
          />
          <Route
            path="/help"
            element={
              <Suspense fallback={<LoadingPage />}>
                <Help />
              </Suspense>
            }
          />
          {/* Plaid OAuth returns to /app/plaid-oauth as an iOS Universal Link /
              Android App Link — the OS reopens the native app. This catch-all is
              only the fallback landing shown if that interception does not fire. */}
          <Route path="/app/*" element={<InstallLanding />} />
          <Route path="/*" element={<InstallLanding />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  </MantineProvider>
);

export default App;
